/**
 * **La barre de recherche de la barre supérieure, et ce qu'elle fait enfin.**
 *
 * `AppShell` la rendait `<input type="search">` sans le moindre gestionnaire.
 * Elle ne faisait rien, elle n'avait jamais rien fait, et elle était sur les
 * **seize pages** — avec, sur chacune, l'invite que la maquette lui donne :
 * « Référence, numéro, montant… » sur les transactions, « Compte, scénario,
 * dossier… » sur la conformité. Une invite qui décrit précisément ce qu'on
 * pourrait chercher, au-dessus d'un champ qui ne cherche rien, est la forme la
 * plus coûteuse du bouton inerte : elle ne se contente pas de ne rien faire,
 * elle promet.
 *
 * ## Ni la câbler partout, ni la retirer partout
 *
 * C'est exactement le raisonnement d'`export-context.tsx`, et il vaut mot pour
 * mot. La câbler dans la coquille était impossible **honnêtement** : la
 * coquille ne sait pas ce qu'un écran cherche, et sur les dix écrans provisoires
 * il n'y a rien à chercher. La retirer partout aurait effacé du dessin un
 * élément que la maquette pose sur chaque artboard.
 *
 * **Elle est donc branchée là où elle marche, et nulle part ailleurs.** Un
 * écran qui sait chercher déclare son gestionnaire ; la barre le lui passe. Un
 * écran qui ne sait pas chercher n'en déclare aucun, et la frappe ne part nulle
 * part — c'est le comportement d'aujourd'hui, inchangé sur les quinze autres
 * modules.
 *
 * ## Pourquoi `Entrée`, et non à chaque frappe
 *
 * **Parce que chercher est un acte journalisé.** `EX-BO-004` : le serveur écrit
 * une entrée d'`admin.audit_log` par consultation. Une recherche qui partirait
 * à chaque frappe écrirait une entrée par **lettre** — treize lignes de journal
 * pour « TXBORETRAIT01 », et un journal d'audit qu'on ne peut plus lire. Ce
 * n'est donc pas un choix d'ergonomie contre un autre : c'est la seule des deux
 * conduites qui laisse le journal utilisable.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * La recherche d'un écran : **ce qu'il cherche déjà**, et ce qu'il faut appeler.
 *
 * `term` a été ajouté après mesure, et il répare une faute que la revue a
 * relevée : après une recherche puis un retour en arrière, le champ de la barre
 * affichait encore `779982462` pendant que la table rendait la file entière.
 * **L'interface affirmait un filtre qui n'était pas appliqué** — le pire des
 * trois états possibles, parce qu'un écran vide se remarque et qu'un écran qui
 * ment, non.
 *
 * La barre ne retient donc plus rien d'elle-même : elle affiche ce que l'écran
 * déclare chercher, et l'écran le tient dans l'adresse.
 */
export interface TopbarSearch {
  /** Le terme actuellement appliqué — celui que l'écran affiche vraiment. */
  readonly term: string;
  /** Ce qu'il faut appeler avec le terme saisi. */
  readonly onSearch: (term: string) => void;
}

/** Ce que la barre supérieure sait de la recherche du moment. */
interface SearchRegistry {
  /** La recherche de l'écran courant, ou `null` s'il ne cherche pas. */
  readonly run: TopbarSearch | null;
  /** Déclare — ou retire — la recherche de l'écran courant. */
  readonly declare: (run: TopbarSearch | null) => void;
}

const Contexte = createContext<SearchRegistry | null>(null);

/** Porte la déclaration de recherche au travers de la coquille. */
export function SearchProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [run, setRun] = useState<TopbarSearch | null>(null);
  // `setRun(() => …)` et non `setRun(…)` : React traiterait une valeur passée
  // nue comme une mise à jour fonctionnelle si elle était une fonction. Elle ne
  // l'est plus — c'est un objet —, mais la forme est gardée : le jour où la
  // déclaration redeviendrait une fonction, la faute serait invisible.
  const declare = useCallback((commande: TopbarSearch | null) => {
    setRun(() => commande);
  }, []);
  const valeur = useMemo(() => ({ run, declare }), [declare, run]);
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

/** Ce que la barre supérieure lit. Hors coquille, aucune recherche n'est déclarée. */
export function useTopbarSearch(): TopbarSearch | null {
  return useContext(Contexte)?.run ?? null;
}

/**
 * Déclare la recherche de l'écran courant, et la **retire à son démontage**.
 *
 * Sans le retrait, le gestionnaire survivrait au changement d'écran et une
 * frappe dans la barre du module suivant filtrerait le registre du précédent —
 * sans rien afficher, puisque l'écran qui l'affiche n'est plus monté.
 */
export function useDeclareSearch(run: TopbarSearch | null): void {
  const registre = useContext(Contexte);
  const declare = registre?.declare;
  useEffect(() => {
    if (declare === undefined) return undefined;
    declare(run);
    return () => {
      declare(null);
    };
  }, [declare, run]);
}
